import { useEffect, useState } from "react";
import { ColorsService } from "../../services/colors.service";
import {
  ColorsDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import { FaPlus } from "react-icons/fa";

const Colors = () => {
  const [colors, setColors] = useState<ColorsDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<ColorsDataType | null>(
    null
  );
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState("AZ");

  useEffect(() => {
    setLoading(true);
    ColorsService.getAllColors()
      .then((response) => {
        if (Array.isArray(response?.data?.colors)) {
          const dataWithKeys = response?.data?.colors?.map(
            (item: ColorsDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setColors(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await ColorsService.getAllColors();
    setColors(
      updatedData?.data?.colors.map((item: ColorsDataType, index: number) => ({
        ...item,
        key: index + 1,
      }))
    );
  };
  const {
    handleSubmit,
    values,
    resetForm,
    setFieldValue,
    touched,
    errors,
    handleChange,
  } = useFormik({
    initialValues: {
      name: "",
      hex: "",
      translations: [] as { lang_code: string; text: string }[],
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Rəng adı tələb olunur"),
    }),
    onSubmit: async (values) => {
      const azTranslation = {
        lang_code: "AZ",
        text: values.name,
      };

      const updatedTranslations = [
        azTranslation,
        ...values.translations.filter((t) => t.lang_code !== "AZ"),
      ];
      const finalValues = {
        ...values,
        translations: updatedTranslations,
      };

      if (isEditing && editingRecord) {
        const isEqual =
          editingRecord.name === values.name &&
          editingRecord.hex === values.hex &&
          editingRecord.translations === values.translations;
        if (isEqual) {
          message.info("Dəyişiklik edilmədi.");
        } else {
          const response = await ColorsService.updateColors({
            ...finalValues,
            id: editingRecord.id,
          });
          if (response.status === 200) {
            message.success("Rəng uğurla yeniləndi!");
            await handleAfterSubmit();
          } else {
            message.error(
              response.errorMessage || "Rəngi ​​yeniləmək alınmadı."
            );
          }
        }
      } else {
        const response = await ColorsService.addColors(finalValues);
        if (response.status === 200) {
          message.success("Rəng uğurla əlavə edildi!");
          await handleAfterSubmit();
        } else {
          message.error(response.errorMessage || "Rəng əlavə etmək alınmadı.");
        }
      }
      setLoading(false);
    },
  });
  const handleTranslationChange = (value: string) => {
    if (langCode === "AZ") {
      setFieldValue("name", value);
    } else {
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );

      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          text: value,
        };
      } else {
        updatedTranslations.push({ lang_code: langCode, text: value });
      }

      setFieldValue("translations", updatedTranslations);
    }
  };
  const handleDeleteClick = async (record: ColorsDataType) => {
    try {
      await ColorsService.deleteColors(record.id.toString());
      setColors((prevData) => prevData.filter((item) => item.id !== record.id));
      message.success("Rəng uğurla silindi!");
    } catch (error) {
      message.error("Rəngi ​​silmək alınmadı.");
    }
  };

  const columns: TableProps<ColorsDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Rəng adı",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Rıng kodu",
      dataIndex: "hex",
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu rəngi siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info(" Silmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setLangCode("AZ");
  };
  const handleEditClick = (record: ColorsDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("name", record.name);
    setFieldValue("hex", record.hex);
    setFieldValue("translations", record.translations || []);
    setLangCode("AZ");
  };

  return (
    <div id="pages">
      <Helmet>
        <title>BSC | Rənglər</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Rənglər</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<ColorsDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={colors}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={isEditing ? "Rəngi ​​Redaktə et" : "Yeni Rəng əlavə edin"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={setLangCode}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />
          <div className="form_input">
            <label className="label" htmlFor="name">
              Rəng adı {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="name"
                id="name"
                placeholder="Rəng adı"
                value={
                  langCode === "AZ"
                    ? values.name
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.text || ""
                }
                onChange={(e) => handleTranslationChange(e.target.value)}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.name} touched={touched.name} />
          </div>
          <div className="form_input">
            <label className="label" htmlFor="hex">
              Rəng kodu
            </label>
            <div className="input_wrapper">
              <input
                name="hex"
                id="hex"
                placeholder="Rəng kodu"
                value={values.hex || ""}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
          </div>
          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Colors;
